import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { RelationshipSelector, resolveRelationship, isRelationshipMissing } from '../../components/RelationshipSelector';
import { UpgradeNudge } from './shared';

const EMPTY_FORM = { name: '', email: '', phone: '', relationship: '', relationship_other: '', permission_level: 'admin' };

export function AdminsTab({ memorialId, isOwner, summary }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(() => api.get(`/memorials/${memorialId}/admins`).then(({ data }) => setAdmins(data.admins)), [memorialId]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.name || !form.email) {
      toast.error('Please add a name and email.');
      return;
    }
    if (isRelationshipMissing(form.relationship, form.relationship_other)) {
      toast.error('Please choose a relationship (or type one when "Other" is selected).');
      return;
    }
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        permission_level: form.permission_level,
        relationship: resolveRelationship(form.relationship, form.relationship_other),
      };
      const { data } = await api.post(`/memorials/${memorialId}/admins`, payload);
      setLastInvite(data);
      setForm(EMPTY_FORM);
      const delivered = data?.delivery?.delivered;
      toast.success(
        delivered
          ? 'Invitation sent. Ask them to check their spam or junk folder if it is not in their inbox within a few minutes.'
          : 'Admin added. Copy the invite link below and send it to them directly.',
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (aid) => {
    if (!window.confirm('Remove admin?')) return;
    await api.delete(`/memorials/${memorialId}/admins/${aid}`); load();
  };

  return (
    <div className="space-y-5">
      {summary && <UpgradeNudge summary={summary} variant="inline" scope="admins" />}
      {isOwner && (
        <div className="ml-card">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Invite a family admin</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <input className="ml-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" />
            <input className="ml-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" />
            <input className="ml-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-phone" />
            <div>
              <RelationshipSelector
                preset="admin"
                value={form.relationship}
                customValue={form.relationship_other}
                onChange={(rel, other) => setForm({ ...form, relationship: rel, relationship_other: other })}
                placeholder="Relationship…"
                selectTestId="admin-relationship-select"
                inputTestId="admin-relationship-other"
              />
            </div>
            <select className="ml-input" value={form.permission_level} onChange={(e) => setForm({ ...form, permission_level: e.target.value })}>
              <option value="admin">Admin</option>
              <option value="contributor">Contributor</option>
            </select>
          </div>
          <p className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))] italic">
            Admins are immediate family only (Son, Daughter, Father, Mother). Other relatives can be added to the Legacy Tree.
          </p>
          <button onClick={invite} className="ml-btn-primary mt-3" data-testid="invite-admin-btn">Send invite</button>

          {lastInvite && (
            <div className="mt-5 ml-card !p-4 border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5" data-testid="last-invite-card">
              <div className="ml-overline">Invitation sent</div>
              <div className="mt-1 text-sm"><strong>{lastInvite.name}</strong> · {lastInvite.invited_email} · {lastInvite.relationship}</div>
              {lastInvite.delivery && lastInvite.delivery.delivered !== false && (
                <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]" data-testid="invite-spam-hint">
                  Please ask {lastInvite.name?.split(' ')[0] || 'them'} to check their spam or junk folder if they don't see it within a few minutes.
                </div>
              )}
              {lastInvite.delivery && !lastInvite.delivery.delivered && (
                <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic">
                  {lastInvite.delivery.reason === 'not_configured'
                    ? 'Email provider not configured yet — copy the invite link below and send it manually.'
                    : 'Email delivery failed — copy the link below and send it manually.'}
                </div>
              )}
              {lastInvite.invite_url && (
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <div className="font-mono text-xs break-all flex-1 min-w-0">{lastInvite.invite_url}</div>
                  <button className="ml-btn-outline !py-1.5 !px-4 text-xs" data-testid="copy-invite-link-btn"
                          onClick={() => navigator.clipboard.writeText(lastInvite.invite_url)}>Copy</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="space-y-2">
        {admins.map((a) => (
          <div key={a.admin_id} className="ml-card flex justify-between items-center">
            <div>
              <div className="font-medium">{a.name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.invited_email} · {a.relationship} · {a.permission_level}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="ml-overline !text-[hsl(var(--accent))]">{a.status}</span>
              {isOwner && <button onClick={() => remove(a.admin_id)} className="ml-btn-outline !py-1.5 text-xs">Remove</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
